import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check } from "lucide-react";
import { type ReactNode, useId } from "react";

import { cn } from "@/shadcn/lib/utils";

export type CheckboxProps = {
  /** Controlled checked state. There is deliberately no indeterminate/"mixed" state. */
  "checked": boolean;
  "disabled"?: boolean;
  /** Shows the choice without letting it change: only the box dims, the label reads as normal. */
  "readOnly"?: boolean;
  "name"?: string;
  "className"?: string;
  "aria-label"?: string;
  "data-testid"?: string;
  "onCheckedChange"?: (checked: boolean) => void;
  /** Visible label text. It sits inside the label, so clicking it toggles the box. */
  "children"?: ReactNode;
};

/**
 * Sanctioned checkbox primitive. Visually identical to the frozen `atoms/Checkbox`
 * (success-green checked state, `<label>` wrapper, 24px box) so surfaces can move off
 * the legacy layer without a visual diff.
 */
export const Checkbox = ({
  checked,
  disabled = false,
  readOnly = false,
  name,
  className,
  "aria-label": ariaLabel,
  "data-testid": dataTestId,
  onCheckedChange,
  children,
}: CheckboxProps) => {
  const labelTextId = useId();
  // A `<label>` names an `<input>`, not the button Radix renders in its place: browsers read
  // the box's name from aria-labelledby, so the label text is referenced explicitly.
  const hasLabelText = children != null && ariaLabel == null;

  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control -- the control IS nested: `CheckboxPrimitive.Root` renders a `role="checkbox"` button inside this label, which the rule cannot resolve through the component boundary
    <label
      className={cn(
        "flex items-center",
        readOnly && "cursor-default",
        disabled && "cursor-not-allowed opacity-30",
        !readOnly && !disabled && "cursor-pointer",
        className,
      )}
    >
      <CheckboxPrimitive.Root
        aria-label={ariaLabel}
        aria-labelledby={hasLabelText ? labelTextId : undefined}
        checked={checked}
        className={cn(
          "m-2.5 flex h-6 w-6 shrink-0 items-center justify-center rounded border border-solid border-border-token-outline bg-white ring-0 ring-transparent hover:border-border-token-strong focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-hidden",
          "data-[state=checked]:border-status-success data-[state=checked]:bg-status-success data-[state=checked]:text-white",
          readOnly && "cursor-default opacity-30",
          disabled && "cursor-not-allowed opacity-30",
          !readOnly && !disabled && "cursor-pointer",
        )}
        data-testid={dataTestId}
        disabled={readOnly || disabled}
        name={name}
        onCheckedChange={(value) => {
          // Radix models tri-state; this primitive is boolean-only by design.
          if (value === "indeterminate") {
            return;
          }
          onCheckedChange?.(value);
        }}
      >
        <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
          <Check className="h-4 w-4" strokeWidth={3} />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      {hasLabelText ? (
        <span className="contents" id={labelTextId}>
          {children}
        </span>
      ) : (
        children
      )}
    </label>
  );
};
